import React, { useState, useRef, useCallback } from 'react';
import { runAiJob } from '../lib/aiJob';
import { MarkdownText } from '../components/MarkdownText';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  FileText,
  Clock,
  Shield,
  Scale,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle,
  Calendar,
  Building2,
  MapPin,
  DollarSign,
  User,
  FileQuestion,
  Gavel,
  Upload,
  ChevronDown,
  ChevronUp,
  Copy,
  Check,
  Info,
  Ban,
  Phone,
  Mail,
  ClipboardList,
  Target,
  XCircle,
  HelpCircle,
  File,
  Image,
  FileType,
  X,
  Eye
} from 'lucide-react';

// Supported file types
const SUPPORTED_FILES = {
  'application/pdf': { icon: FileText, label: 'PDF', color: 'red' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileType, label: 'Word', color: 'blue' },
  'application/msword': { icon: FileType, label: 'Word', color: 'blue' },
  'text/plain': { icon: FileText, label: 'Texto', color: 'slate' },
  'image/jpeg': { icon: Image, label: 'Imagen', color: 'green' },
  'image/png': { icon: Image, label: 'Imagen', color: 'green' },
  'image/gif': { icon: Image, label: 'Imagen', color: 'green' },
  'image/webp': { icon: Image, label: 'Imagen', color: 'green' },
  'image/bmp': { icon: Image, label: 'Imagen', color: 'green' },
  'application/rtf': { icon: FileText, label: 'RTF', color: 'orange' }
};

const TIPOS_NOTIFICACION = [
  { value: 'tgr', label: 'Notificación de TGR (Tesorería General)', icon: Building2, color: 'red' },
  { value: 'demanda', label: 'Demanda Judicial', icon: Scale, color: 'amber' },
  { value: 'embargo', label: 'Mandamiento de Ejecución / Embargo', icon: AlertTriangle, color: 'red' },
  { value: 'apelacion', label: 'Recurso de Apelación', icon: FileText, color: 'purple' },
  { value: 'jpl', label: 'Citación Juzgado de Policía Local', icon: Gavel, color: 'blue' },
  { value: 'cobranza', label: 'Cobranza Judicial', icon: DollarSign, color: 'orange' },
  { value: 'familia', label: 'Notificación Tribunal de Familia', icon: User, color: 'pink' },
  { value: 'laboral', label: 'Notificación Laboral', icon: Building2, color: 'green' },
  { value: 'otra', label: 'Otra notificación', icon: FileQuestion, color: 'slate' }
];

const FORMAS_NOTIFICACION = [
  'Personal (en persona)',
  'Cédula (dejada en domicilio)',
  'Carta certificada',
  'Receptor judicial',
  'Correo electrónico',
  'Portal web / sistema en línea',
  'Publicación en diario',
  'Estado diario',
  'No estoy seguro'
];

const MATERIAS = [
  'Civil',
  'Cobranza / Ejecutivo',
  'Tránsito / Infracciones',
  'Municipal / Patentes',
  'Familia',
  'Laboral',
  'Tributario',
  'Penal',
  'Consumidor',
  'Administrativa',
  'Otra'
];

const ESTADOS_CASO = [
  'Primera notificación / inicio del caso',
  'Ya hubo audiencia previa',
  'Existe sentencia de primera instancia',
  'En etapa de ejecución',
  'En apelación',
  'Tiene sentencia firme',
  'No estoy seguro del estado'
];

const OBJETIVOS = [
  { value: 'defender', label: 'Defenderme / Oponerme', icon: Shield },
  { value: 'negociar', label: 'Negociar / Llegar a acuerdo', icon: Target },
  { value: 'pagar', label: 'Pagar y regularizar', icon: DollarSign },
  { value: 'suspender', label: 'Suspender medida / embargo', icon: Ban },
  { value: 'apelar', label: 'Apelar / Impugnar', icon: Scale },
  { value: 'aclarar', label: 'Aclarar situación / Entender qué pasa', icon: HelpCircle },
  { value: 'plazo', label: 'Pedir prórroga / plazo', icon: Clock }
];

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = false, color = 'purple' }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-5 py-4 flex items-center justify-between bg-slate-800/50 hover:bg-slate-800 transition-colors`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 text-${color}-400`} />
          <span className="font-semibold text-white">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-5 bg-slate-900/50">
          {children}
        </div>
      )}
    </div>
  );
};

const LegalTriagePage = () => {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    tipoNotificacion: '',
    fechaRecepcion: '',
    formaNotificacion: '',
    institucion: '',
    comuna: '',
    materia: '',
    estadoCaso: '',
    hayEmbargo: '',
    monto: '',
    tieneAbogado: '',
    yaRealizoGestion: '',
    objetivo: '',
    textoNotificacion: '',
    documentosDisponibles: '',
    extractedFileText: ''
  });
  
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('form'); // 'form' | 'result'
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // File upload handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const processFile = async (file) => {
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo es muy grande. Máximo 10MB.');
      return;
    }
    
    setUploadingFile(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/legal-triage/upload-document', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const newFile = {
          id: Date.now(),
          name: file.name,
          type: response.data.file_type,
          size: response.data.file_size,
          extractedText: response.data.extracted_text,
          textLength: response.data.text_length
        };
        
        setUploadedFiles(prev => [...prev, newFile]);
        
        // Append extracted text to form data
        setFormData(prev => ({
          ...prev,
          extractedFileText: prev.extractedFileText 
            ? `${prev.extractedFileText}\n\n--- DOCUMENTO: ${file.name} ---\n${response.data.extracted_text}`
            : `--- DOCUMENTO: ${file.name} ---\n${response.data.extracted_text}`
        }));
        
        toast.success(`${file.name} procesado correctamente`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Error al procesar el archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await processFile(e.dataTransfer.files[0]);
    }
  }, [api]);

  const handleFileSelect = async (e) => {
    if (e.target.files && e.target.files[0]) {
      await processFile(e.target.files[0]);
    }
  };

  const removeFile = (fileId) => {
    const fileToRemove = uploadedFiles.find(f => f.id === fileId);
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    
    // Remove from extracted text
    if (fileToRemove) {
      setFormData(prev => ({
        ...prev,
        extractedFileText: prev.extractedFileText
          .replace(`--- DOCUMENTO: ${fileToRemove.name} ---\n${fileToRemove.extractedText}`, '')
          .replace(/\n\n+/g, '\n\n')
          .trim()
      }));
    }
    toast.success('Archivo eliminado');
  };

  const handleSubmit = async () => {
    if (!formData.tipoNotificacion) {
      toast.error('Selecciona el tipo de notificación');
      return;
    }

    setLoading(true);
    try {
      const result = await runAiJob(api, '/legal-triage/analyze', formData);
      setAnalysis(result.analysis);
      setActiveTab('result');
      toast.success('Análisis completado');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Error al analizar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (analysis) {
      navigator.clipboard.writeText(analysis);
      setCopied(true);
      toast.success('Análisis copiado');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const selectedTipo = TIPOS_NOTIFICACION.find(t => t.value === formData.tipoNotificacion);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-red-900/20">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-slate-400" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  Consejero Legal
                </h1>
                <p className="text-sm text-slate-400">Asistente de notificaciones legales en Chile</p>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('form')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'form' 
                    ? 'bg-red-600 text-white' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                Ingresar Datos
              </button>
              <button
                onClick={() => setActiveTab('result')}
                disabled={!analysis}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'result' 
                    ? 'bg-red-600 text-white' 
                    : 'text-slate-400 hover:text-white disabled:opacity-50'
                }`}
              >
                Ver Análisis
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Alert Banner */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <p className="text-amber-200 font-medium">Aviso Importante</p>
              <p className="text-sm text-amber-200/70">
                Esta herramienta proporciona orientación general y no reemplaza la asesoría legal profesional. 
                En casos complejos o de alto riesgo, consulte con un abogado.
              </p>
            </div>
          </div>
        </div>

        {activeTab === 'form' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tipo de Notificación */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-400" />
                  ¿Qué tipo de notificación recibiste?
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {TIPOS_NOTIFICACION.map((tipo) => {
                    const Icon = tipo.icon;
                    const isSelected = formData.tipoNotificacion === tipo.value;
                    return (
                      <button
                        key={tipo.value}
                        onClick={() => handleInputChange('tipoNotificacion', tipo.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? `bg-${tipo.color}-500/20 border-${tipo.color}-500`
                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mb-2 text-${tipo.color}-400`} />
                        <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                          {tipo.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Datos Básicos */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-400" />
                  Información de la Notificación
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Fecha de Recepción
                    </label>
                    <input
                      type="date"
                      value={formData.fechaRecepcion}
                      onChange={(e) => handleInputChange('fechaRecepcion', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Forma de Notificación
                    </label>
                    <select
                      value={formData.formaNotificacion}
                      onChange={(e) => handleInputChange('formaNotificacion', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      {FORMAS_NOTIFICACION.map(f => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Tribunal / Institución
                    </label>
                    <input
                      type="text"
                      value={formData.institucion}
                      onChange={(e) => handleInputChange('institucion', e.target.value)}
                      placeholder="Ej: 1° Juzgado Civil de Santiago"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Comuna / Ciudad
                    </label>
                    <input
                      type="text"
                      value={formData.comuna}
                      onChange={(e) => handleInputChange('comuna', e.target.value)}
                      placeholder="Ej: Santiago"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Scale className="w-4 h-4" />
                      Materia
                    </label>
                    <select
                      value={formData.materia}
                      onChange={(e) => handleInputChange('materia', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      {MATERIAS.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      Estado del Caso
                    </label>
                    <select
                      value={formData.estadoCaso}
                      onChange={(e) => handleInputChange('estadoCaso', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      {ESTADOS_CASO.map(e => (
                        <option key={e} value={e}>{e}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Situación Actual */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Situación Actual
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300">
                      ¿Existe embargo, retención o apremio?
                    </label>
                    <select
                      value={formData.hayEmbargo}
                      onChange={(e) => handleInputChange('hayEmbargo', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si_embargo">Sí, hay orden de embargo</option>
                      <option value="si_retencion">Sí, hay retención de fondos</option>
                      <option value="si_apremio">Sí, hay apremio (arresto)</option>
                      <option value="no">No</option>
                      <option value="no_se">No estoy seguro</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Monto Involucrado (si aplica)
                    </label>
                    <input
                      type="text"
                      value={formData.monto}
                      onChange={(e) => handleInputChange('monto', e.target.value)}
                      placeholder="Ej: $1.500.000"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300">
                      ¿Tiene abogado?
                    </label>
                    <select
                      value={formData.tieneAbogado}
                      onChange={(e) => handleInputChange('tieneAbogado', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="si">Sí</option>
                      <option value="no">No</option>
                      <option value="buscando">Estoy buscando uno</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300">
                      ¿Ya realizó alguna gestión?
                    </label>
                    <select
                      value={formData.yaRealizoGestion}
                      onChange={(e) => handleInputChange('yaRealizoGestion', e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-red-500 focus:outline-none"
                    >
                      <option value="">Seleccionar...</option>
                      <option value="no">No, nada aún</option>
                      <option value="pago_parcial">Sí, pagué parcialmente</option>
                      <option value="pago_total">Sí, pagué todo</option>
                      <option value="escrito">Sí, presenté escrito/descargos</option>
                      <option value="compareci">Sí, comparecí a audiencia</option>
                      <option value="contacte">Sí, contacté al demandante</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Objetivo */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-400" />
                  ¿Cuál es tu objetivo?
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {OBJETIVOS.map((obj) => {
                    const Icon = obj.icon;
                    const isSelected = formData.objetivo === obj.value;
                    return (
                      <button
                        key={obj.value}
                        onClick={() => handleInputChange('objetivo', obj.value)}
                        className={`p-3 rounded-xl border-2 text-center transition-all ${
                          isSelected
                            ? 'bg-green-500/20 border-green-500'
                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-2 ${isSelected ? 'text-green-400' : 'text-slate-400'}`} />
                        <p className={`text-xs font-medium ${isSelected ? 'text-white' : 'text-slate-400'}`}>
                          {obj.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* File Upload Section */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-emerald-400" />
                  Subir Documento (PDF, Word, Imagen)
                </h3>
                
                {/* Drag and Drop Zone */}
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                    dragActive 
                      ? 'border-emerald-500 bg-emerald-500/10' 
                      : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.txt,.rtf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.heic"
                    className="hidden"
                  />
                  
                  {uploadingFile ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mb-3" />
                      <p className="text-slate-300">Procesando documento...</p>
                      <p className="text-xs text-slate-500 mt-1">Extrayendo texto con IA</p>
                    </div>
                  ) : (
                    <div 
                      className="cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Upload className="w-8 h-8 text-slate-400" />
                      </div>
                      <p className="text-slate-300 font-medium mb-1">
                        Arrastra tu documento aquí o haz clic para seleccionar
                      </p>
                      <p className="text-sm text-slate-500">
                        PDF, Word, RTF, Texto plano o Imágenes (máx. 10MB)
                      </p>
                      <div className="flex flex-wrap justify-center gap-2 mt-4">
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded text-xs">PDF</span>
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">Word</span>
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">JPG/PNG</span>
                        <span className="px-2 py-1 bg-slate-500/20 text-slate-400 rounded text-xs">TXT</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Uploaded Files List */}
                {uploadedFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm text-slate-400 font-medium">Documentos cargados:</p>
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                            <FileText className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-white truncate max-w-[200px]">{file.name}</p>
                            <p className="text-xs text-slate-500">
                              {file.type} • {(file.size / 1024).toFixed(1)} KB • {file.textLength} caracteres extraídos
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              toast.info(file.extractedText?.substring(0, 500) + '...' || 'Sin texto extraído');
                            }}
                            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                            title="Ver texto extraído"
                          >
                            <Eye className="w-4 h-4 text-slate-400" />
                          </button>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Texto de la Notificación */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  O escribe/pega el texto manualmente
                </h3>
                <textarea
                  value={formData.textoNotificacion}
                  onChange={(e) => handleInputChange('textoNotificacion', e.target.value)}
                  placeholder="Si no puedes subir el documento, copia y pega aquí el texto de la notificación..."
                  rows={4}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none resize-none"
                />
                <p className="text-xs text-slate-500 mt-2">
                  {formData.extractedFileText 
                    ? `✓ Ya tienes ${uploadedFiles.length} documento(s) cargado(s). Este texto es adicional.`
                    : 'Entre más detalle proporciones, más preciso será el análisis'
                  }
                </p>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                data-testid="triage-submit-btn"
                disabled={loading || !formData.tipoNotificacion}
                className="w-full py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-semibold flex items-center justify-center gap-3 transition-all"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Analizando tu caso...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Analizar y Obtener Plan de Acción
                  </>
                )}
              </button>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Selected Type Info */}
              {selectedTipo && (
                <div className={`bg-${selectedTipo.color}-500/10 border border-${selectedTipo.color}-500/30 rounded-xl p-5`}>
                  <div className="flex items-center gap-3 mb-3">
                    <selectedTipo.icon className={`w-6 h-6 text-${selectedTipo.color}-400`} />
                    <h4 className="font-semibold text-white">{selectedTipo.label}</h4>
                  </div>
                  <p className="text-sm text-slate-300">
                    {selectedTipo.value === 'tgr' && 'Notificación de cobro de deudas fiscales, multas o impuestos.'}
                    {selectedTipo.value === 'demanda' && 'Inicio formal de un proceso judicial en tu contra.'}
                    {selectedTipo.value === 'embargo' && 'Orden de retención o incautación de bienes por deuda.'}
                    {selectedTipo.value === 'apelacion' && 'Recurso contra una resolución judicial previa.'}
                    {selectedTipo.value === 'jpl' && 'Citación por infracción de tránsito u ordenanza municipal.'}
                    {selectedTipo.value === 'cobranza' && 'Proceso de cobro ejecutivo de una deuda.'}
                    {selectedTipo.value === 'familia' && 'Asuntos de pensión alimenticia, visitas, tuición, etc.'}
                    {selectedTipo.value === 'laboral' && 'Demanda o citación en materia del trabajo.'}
                    {selectedTipo.value === 'otra' && 'Otra notificación legal o administrativa.'}
                  </p>
                </div>
              )}

              {/* Help Tips */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-blue-400" />
                  Tips para mejor análisis
                </h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Indica la fecha exacta de recepción</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Copia el texto de la resolución si lo tienes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Especifica el tribunal y ROL de la causa</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>Indica si ya hiciste alguna gestión</span>
                  </li>
                </ul>
              </div>

              {/* Emergency Contact */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5">
                <h4 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  ¿Emergencia Legal?
                </h4>
                <p className="text-sm text-slate-300 mb-3">
                  Si tienes un embargo en curso o citación en las próximas 24 horas, 
                  contacta a un abogado de inmediato.
                </p>
                <a 
                  href="tel:+56912345678"
                  className="text-red-400 text-sm font-medium hover:text-red-300"
                >
                  Línea de emergencia legal →
                </a>
              </div>
            </div>
          </div>
        ) : (
          /* Results View */
          <div className="space-y-6">
            {analysis && (
              <>
                {/* Action Bar */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setActiveTab('form')}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Modificar datos
                  </button>
                  <button
                    onClick={handleCopy}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copiado' : 'Copiar análisis'}
                  </button>
                </div>

                {/* Analysis Content */}
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="prose prose-invert max-w-none">
                    <MarkdownText className="text-slate-300 text-sm" data-testid="triage-analysis-result">{analysis}</MarkdownText>
                  </div>
                </div>

                {/* Warning */}
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <p className="text-amber-200 font-medium">Recordatorio</p>
                      <p className="text-sm text-amber-200/70">
                        Este análisis es orientativo. Los plazos y procedimientos pueden variar según 
                        las circunstancias específicas de tu caso. Verifica siempre con el texto 
                        original de la notificación y consulta con un abogado si tienes dudas.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default LegalTriagePage;
